// ============================================
// i18n - Internationalization System
// ============================================

import ar from './ar.json';
import en from './en.json';

export type Language = 'ar' | 'en';

const translations: Record<Language, typeof ar> = { ar, en };

// Get current language from localStorage or default to Arabic
export function getLanguage(): Language {
  if (typeof window === 'undefined') return 'ar';
  const stored = localStorage.getItem('crm-language');
  return (stored === 'en' || stored === 'ar') ? stored : 'ar';
}

// Set language
export function setLanguage(lang: Language): void {
  localStorage.setItem('crm-language', lang);
  // Update document direction
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = lang;
  // Dispatch event for reactivity
  window.dispatchEvent(new CustomEvent('languageChange', { detail: lang }));
}

// Initialize RTL direction on load
export function initializeLanguage(): void {
  const lang = getLanguage();
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = lang;
}

// Get nested value from object using dot notation
function getNestedValue(obj: Record<string, unknown>, path: string): string | undefined {
  const keys = path.split('.');
  let result: unknown = obj;
  
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = (result as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }
  
  return typeof result === 'string' ? result : undefined;
}

// Translation function
export function t(key: string, lang?: Language): string {
  const currentLang = lang || getLanguage();
  const translation = getNestedValue(translations[currentLang] as unknown as Record<string, unknown>, key);
  
  // Fallback to Arabic if not found in current language
  if (!translation && currentLang !== 'ar') {
    return getNestedValue(translations.ar as unknown as Record<string, unknown>, key) || key;
  }
  
  return translation || key;
}

// Export translations for direct access
export { ar, en };
export default { t, getLanguage, setLanguage, initializeLanguage };
